import { useState } from 'react';
import { useAppointments, useCreateAppointment, useContacts } from '@/hooks/useCRM';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, CalendarDays, MapPin, Clock } from 'lucide-react';
import { format } from 'date-fns';
import type { AppointmentStatus, AppointmentType } from '@/types/models';
import { toast } from 'sonner';

const STATUS_STYLES: Record<AppointmentStatus, string> = {
  scheduled: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  completed: 'bg-green-500/15 text-green-700 dark:text-green-300',
  cancelled: 'bg-destructive/15 text-destructive',
};

const TYPE_LABELS: Record<string, string> = {
  call: '📞 Call',
  meeting: '🤝 Meeting',
  site_visit: '🏢 Site Visit',
};

export default function AppointmentsPage() {
  const { data: appointments = [], isLoading } = useAppointments();
  const createAppointment = useCreateAppointment();
  const { data: contacts = [] } = useContacts();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', type: 'meeting' as AppointmentType, contact_id: '',
    start_at: '', end_at: '', location: '', notes: '',
  });

  const sorted = [...appointments].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());

  const handleCreate = () => {
    if (!form.title || !form.contact_id || !form.start_at || !form.end_at) {
      toast.error('Title, contact, start, and end time are required');
      return;
    }
    createAppointment.mutate(form, {
      onSuccess: () => {
        toast.success('Appointment created');
        setCreateOpen(false);
        setForm({ title: '', type: 'meeting', contact_id: '', start_at: '', end_at: '', location: '', notes: '' });
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create appointment'),
    });
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Appointments" subtitle="Scheduled meetings, calls, and site visits">
        <Button className="gap-2" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> Schedule</Button>
      </PageHeader>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No appointments scheduled</div>
      ) : (
        <div className="grid gap-3">
          {sorted.map(apt => (
            <Card key={apt.id}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="hidden flex-col items-center sm:flex">
                  <span className="text-2xl font-bold">{format(new Date(apt.start_at), 'd')}</span>
                  <span className="text-xs text-muted-foreground">{format(new Date(apt.start_at), 'MMM')}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{apt.title}</p>
                    <Badge className={STATUS_STYLES[apt.status]}>{apt.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{TYPE_LABELS[apt.type]} with {apt.contact_name} — {apt.customer_name}</p>
                  <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{format(new Date(apt.start_at), 'h:mm a')} – {format(new Date(apt.end_at), 'h:mm a')}</span>
                    {apt.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{apt.location}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
            <DialogDescription>Create a new meeting, call, or site visit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Contract review meeting" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v as AppointmentType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">📞 Call</SelectItem>
                    <SelectItem value="meeting">🤝 Meeting</SelectItem>
                    <SelectItem value="site_visit">🏢 Site Visit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Contact *</Label>
                <Select value={form.contact_id} onValueChange={v => setForm(p => ({ ...p, contact_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Start *</Label>
                <Input type="datetime-local" value={form.start_at} onChange={e => setForm(p => ({ ...p, start_at: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>End *</Label>
                <Input type="datetime-local" value={form.end_at} onChange={e => setForm(p => ({ ...p, end_at: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Office, Zoom link, etc." />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createAppointment.isPending}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
